import React, { useState, useEffect, FC, JSX, ReactNode } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../context/CompanyContext'; // Import useCompany
import { useAgent } from '../../context/AgentContext'; // Import useAgent
import Button from '../ui/Button';
import Dropdown, { DropdownItem, DropdownDivider } from '../ui/Dropdown';
import NotificationDropdown from '../ui/NotificationDropdown';
import { SimplifiedCompanyStatus } from '../../utils/authUtils';
import { DashboardLayoutProps, Notification } from '../../types/components.d';
import { BasicUserDetails, CompanyVerificationStatus } from '../../types/api';
import Footer from './Footer';


const DashboardLayout: FC<DashboardLayoutProps> = ({ children, companyDetails, agentDetails , jobseekerDetails}): JSX.Element => {
  const { user, logout } = useAuth();
  // const { jobPostCount } = useCompany(); // Use jobPostCount from CompanyContext
  // const { pendingCompaniesCount } = useAgent(); // Use pendingCompaniesCount from AgentContext
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const getStatusBadgeProps = (status: SimplifiedCompanyStatus['status'] ) => {
    switch (status) {
      case 'Verified':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300',
          message: 'Company is Verified',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ),
        };
      case 'Pending':
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-300',
          message: 'Agents are reviewing',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ),
        };
      case 'Rejected':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-300',
          message: 'Company was Rejected',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          ),
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-300',
          message: 'Company Not Registered',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ),
        };
    }
  };

   const getJobSeekerStatusBadgeProps = (paymentStatus : boolean ) => {
    switch (paymentStatus) {
      case true:
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-300',
          message: 'Registered User',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ),
        };
      case false:
        return {
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-300',
          message: 'Registration Pending',
          icon: (
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ),
        };
    }
  };

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = (): void => {
    logout();
  };

  const handleMarkAsRead = (notificationId: number): void => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  const handleDeleteNotification = (notificationId: number): void => {
    setNotifications(prev => 
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  const getPageTitle = (): string => {
    const path = location.pathname;
    const role = user?.role;

    if (path.includes('/dashboard')) return 'Dashboard';

    // Role-aware titles
    if (path.includes('/jobs')) {
      if (role === 'Company' && path.includes('/company')) {
        return 'Job Postings';
      }
      return 'Find Jobs';
    }

    if (path.includes('/applications')) return 'Applications';
    if (path.includes('/profile')) return 'Profile';
    if (path.includes('/company')) return 'Company';
    if (path.includes('/agent')) return 'Agent';
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-[#87DE88]/5 transition-colors duration-300 m-0 p-0 no-horizontal-scroll">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="flex h-screen overflow-hidden w-full m-0 p-0 gap-0 max-w-full">
        {/* Sidebar */}
        <Sidebar 
          isOpen={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          companyStatus={companyDetails?.companyStatus || null} 
          jobPostCount={companyDetails?.jobPostCount || null} // Pass jobPostCount
          pendingCompaniesCount={agentDetails?.pendingCompaniesCount} // Pass pendingCompaniesCount
          isjobseekerProfileCompleted= {jobseekerDetails?.isProfileCompleted}
          // Add other badge counts here as they become available
        />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen w-full overflow-y-auto m-0 no-horizontal-scroll">
          {/* Top Bar */}
          <header className={`bg-white/95 backdrop-blur-xl shadow-soft px-2 sm:px-4 lg:px-6 xl:px-8 py-2 sm:py-3 fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-gray-100 m-0 ${
            isScrolled ? 'shadow-lg border-gray-200' : 'shadow-soft'
          }`}>
            <div className="flex items-center justify-between max-w-full w-full overflow-x-hidden">
              <div className="flex items-center flex-1 min-w-0 mr-2 sm:mr-4">
                {/* Mobile menu button */}
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-primary-lightGreen lg:hidden transition-all duration-300 flex-shrink-0 rounded-xl hover:bg-primary-teaGreen/20 mr-2"
                  onClick={() => setSidebarOpen(true)}
                >
                  <span className="sr-only">Open sidebar</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  </svg>
                </button>
                
                {/* Page title and breadcrumb */}
                <div className="min-w-0 flex-1">
                  <h1 className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-bold gradient-text truncate">
                    {getPageTitle()}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 text-gray-400 mt-0.5 sm:mt-1 hidden sm:block truncate">
                    Welcome back, {user?.firstName || 'User'}! Here's what's happening today.
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 flex-shrink-0 max-w-full overflow-x-hidden">
                {/* Company status badge - responsive */}
                {user?.role === 'Company' && companyDetails?.companyStatus && (
                  <div className={`hidden sm:flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-2 ${getStatusBadgeProps(companyDetails?.companyStatus).bgColor} ${getStatusBadgeProps(companyDetails?.companyStatus).borderColor} border rounded-lg`}>
                    {getStatusBadgeProps(companyDetails?.companyStatus).icon}
                    <span className={`text-xs font-semibold ${getStatusBadgeProps(companyDetails?.companyStatus).textColor} hidden md:inline`}>
                      {getStatusBadgeProps(companyDetails?.companyStatus).message}
                    </span>
                    <span className={`text-xs font-semibold ${getStatusBadgeProps(companyDetails?.companyStatus).textColor} md:hidden`}>
                      {companyDetails?.companyStatus || 'Not Registered'}
                    </span>
                  </div>
                )}
                {user?.role === 'Jobseeker' && jobseekerDetails?.isOnboardingPaymentDone != undefined && (
                  <div className={`sm:flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-2 ${getJobSeekerStatusBadgeProps(jobseekerDetails?.isOnboardingPaymentDone).bgColor} ${getJobSeekerStatusBadgeProps(jobseekerDetails?.isOnboardingPaymentDone).borderColor} border rounded-lg`}>
                    <div className='hidden md:block'>{getJobSeekerStatusBadgeProps(jobseekerDetails?.isOnboardingPaymentDone).icon}</div>
                    <span className={`text-xs font-semibold ${getJobSeekerStatusBadgeProps(jobseekerDetails?.isOnboardingPaymentDone).textColor}`}>
                      {getJobSeekerStatusBadgeProps(jobseekerDetails?.isOnboardingPaymentDone).message}
                    </span>
                  </div>
                )}

                {/* Search button (mobile only) */}
                <button className="p-2 text-gray-500 hover:text-primary-lightGreen sm:hidden transition-all duration-300 rounded-xl hover:bg-primary-teaGreen/20">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>

                {/* Notifications */}
                <button 
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 text-gray-500 hover:text-primary-black focus:outline-none rounded-xl transition-all duration-300 hover:bg-primary-teaGreen/20 group" 
                  aria-label="Notifications"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-3 w-3 sm:h-4 sm:w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold animate-pulse">
                      <span className="text-xs hidden sm:inline">{notifications.filter(n => !n.read).length}</span>
                      <span className="w-1.5 h-1.5 bg-white rounded-full sm:hidden"></span>
                    </span>
                  )}
                </button>

                {/* User menu */}
                <Dropdown
                  trigger={
                    <button className="flex items-center space-x-1 sm:space-x-2 text-sm font-medium text-gray-700 hover:text-primary-black focus:outline-none rounded-xl p-1 sm:p-2 hover:bg-primary-teaGreen/10 transition-all duration-300 group">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-[#87DE88] rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                        <span className="text-primary-black font-semibold text-xs sm:text-sm lg:text-base">
                          {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="hidden md:block text-left">
                        <div className="font-medium text-sm lg:text-base truncate max-w-24 lg:max-w-32">{user?.firstName || user?.email}</div>
                        <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                      </div>
                      <svg className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-300 group-hover:rotate-180 hidden sm:block" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  }
                  position="bottom-right"
                >
                  <DropdownItem
                    onClick={() => {
                      const dashboardLink = user?.role === 'Jobseeker' ? '/user/dashboard' : 
                                          user?.role === 'Company' ? '/company/dashboard' : 
                                          user?.role === 'Agent' ? '/agent/dashboard' : '/login';
                      navigate(dashboardLink);
                    }}
                    className="w-full flex items-center space-x-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                    </svg>
                    <span>Dashboard</span>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem onClick={handleLogout} danger>
                    <div className="flex items-center space-x-2">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Logout</span>
                    </div>
                  </DropdownItem>
                </Dropdown>

                {/* Back to main site */}
                <Link to="/" className="hidden xl:block">
                  <Button variant="outline" size="sm" className="text-xs lg:text-sm px-2 lg:px-4">
                    <svg className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span className="hidden lg:inline">Back to main site</span>
                    <span className="lg:hidden">Back</span>
                  </Button>
                </Link>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 bg-gray-50/50 m-0 pt-20 ">
            <div className="p-2 sm:p-3 lg:p-3 xl:p-4 w-full max-w-screen-2xl mx-auto">
              <div className="w-full">
                {children || <Outlet />}
              </div>
            </div>
          </main>
          <Footer minimal/>
        </div>
      </div>

      {/* Notification Dropdown */}
      <NotificationDropdown
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onDeleteNotification={handleDeleteNotification}
      />
    </div>
  );
};

export default DashboardLayout;
